import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { SuperFundAllocationNameType } from '@/types/CallcentrePortalTypes.ts'
import SuperService from '@/services/SuperService.ts'
import RetirementService from '@/services/RetirementService.ts'
import getChantwestProductFromAccountNumber from '@/composables/superHelpers/getChantwestProductFromAccountNumber.ts'
import SuperObject, { SuperPayload } from '@/types/api/super/SuperObject.ts'
import { useSessionStore } from '@/store/pinia/SessionStore.ts'
import getSuperFundInvestmentOptions from '@/composables/superHelpers/getSuperFundInvestmentOptions.ts'

export type SuperGuaranteeData = {
  minSuperGuarantee: number | null
  maxSuperContributions: number | null
  maxSalaryPerAnnumForSuperGuarantee: number | null
  maxContributionPerAnnumForSuperGuarantee: number | null
}

export interface ISuperStore {
  supersArray: Array<SuperObject>
  activeSuperObject: SuperObject
  superProductInvestmentOptions: Array<SuperFundAllocationNameType>
  superGuaranteeData: SuperGuaranteeData
  getSupersArray: Array<SuperObject>
  getActiveSuperObject: SuperObject
  getSuperProductInvestmentOptions: Array<SuperFundAllocationNameType>
  getSuperGuaranteeData: SuperGuaranteeData
  fetchAllSupers: () => void
  addNewSuperObject: (object: SuperObject) => void
  setupSuper: (accountNumber: string, superId: string) => void
  setActiveSuperObject: (newSuperObject: SuperObject) => void
  updateSuperObject: (id: string, superPayload: SuperPayload) => void
  removeSuperObject: (id: string) => void
  resetSuperProductInvestmentOptions: () => void
  reset: () => void
  checkSuperInvestmentEligibility: () => boolean
}

export const useSuperStore = defineStore('SuperStore', () => {
  const supersArray = ref<Array<SuperObject>>([] as Array<SuperObject>)
  const activeSuperObject = ref<SuperObject>({} as SuperObject)
  // InvestmentOptions
  const superProductInvestmentOptions = ref<SuperFundAllocationNameType[]>()
  // Super Guarantee Data
  const superGuaranteeData = ref<SuperGuaranteeData>({
    minSuperGuarantee: null,
    maxSuperContributions: null,
    maxSalaryPerAnnumForSuperGuarantee: null,
    maxContributionPerAnnumForSuperGuarantee: null,
  })

  const getSupersArray = computed(() => supersArray.value)
  const getActiveSuperObject = computed(() => activeSuperObject.value)
  const getPrimarySuperObject = computed(() => getSupersArray.value[0] || ({} as SuperObject)) // this is ordeed in the backend
  const getSuperProductInvestmentOptions = computed(() => superProductInvestmentOptions.value)
  const getSuperGuaranteeData = computed(() => superGuaranteeData.value)

  const fetchAllSupers = async () => {
    try {
      const response = await SuperService.getAllSupers()
      supersArray.value = response.data.data
      /**
       * If we already have an active super object, set it to the matching or first object.
       */
      if (activeSuperObject.value.id && supersArray.value.length) {
        const activeSuper = supersArray.value.find(
          (superObject) => superObject.id === activeSuperObject.value.id,
        )
        activeSuperObject.value = activeSuper ? activeSuper : supersArray.value[0]
        return
      }

      // Set active super object to the first object in the array (fresh)
      activeSuperObject.value =
        supersArray.value.length > 0 ? supersArray.value[0] : ({} as SuperObject)

      // Get investment options for the active super
      if (activeSuperObject.value.chant_west_product_id)
        getInvestmentOptionsForActiveSuper().catch((e) => {
          throw new Error(e)
        })
    } catch (e) {
      console.error(e)
    }
  }

  const fetchSuperGuaranteeData = async () => {
    try {
      const response = await RetirementService.getSuperGuarantee()
      superGuaranteeData.value = {
        minSuperGuarantee: response.data.sg.rate,
        maxSuperContributions: response.data.max_contribution.amount,
        maxSalaryPerAnnumForSuperGuarantee: response.data.contribution_base.max_salary_pa_for_sg,
        maxContributionPerAnnumForSuperGuarantee: response.data.contribution_base.max_contribution_pa_for_sg,
      }
    } catch (e) {
      console.error('Error fetching super guarantee data:', e)
    }
  }

  // Auto-fetch super guarantee data when store is initialized
  fetchSuperGuaranteeData()

  const getInvestmentOptionsForActiveSuper = async () => {
    superProductInvestmentOptions.value = await getSuperFundInvestmentOptions(
      activeSuperObject.value.chant_west_product_id,
    )
  }

  const setInvestmentOptionsForSuper = async (chantWestProductId: number) => {
    superProductInvestmentOptions.value = await getSuperFundInvestmentOptions(chantWestProductId)
  }

  const addNewSuperObject = (object: SuperObject) => {
    supersArray.value.push(object)
    setActiveSuperObject(supersArray.value[supersArray.value.length - 1])
  }

  const setupSuper = async (accountNumber: string, superId: string) => {
    try {
      const chantwestProduct = getChantwestProductFromAccountNumber(
        accountNumber,
        useSessionStore().getWhitelabelData.superfund_config.super_products,
      )
      // Creates/updates super record in the DB
      await SuperService.updateSuper(superId, chantwestProduct)
      // Update the active super object and supers array
      await fetchAllSupers()
      await getInvestmentOptionsForActiveSuper()
      // Optionally update or replace object(s) within the array as needed
      supersArray.value.map((superObject) =>
        supersArray.value.find((obj) => obj.id === superObject.id || obj),
      )
      return
    } catch (e) {
      console.error('Error setting up super', e)
    }
  }

  const setActiveSuperObject = (newSuperObject: SuperObject) => {
    activeSuperObject.value = newSuperObject
  }

  const updateSuperObject = async (id = activeSuperObject.value.id, superPayload: SuperPayload) => {
    try {
      const updatedSuper = await SuperService.updateSuper(id, superPayload)
      activeSuperObject.value = updatedSuper.data.data
      const indexToUpdate = supersArray.value.findIndex((superObject) => superObject.id === id)
      supersArray.value[indexToUpdate] = updatedSuper.data.data
      // Retrieve updated investment options for the active super
      getInvestmentOptionsForActiveSuper().catch((e) => {
        throw new Error(e)
      })
    } catch (e) {
      throw new Error('Error updating super')
    }
  }

  const removeSuperObject = (id: string) => {
    try {
      supersArray.value = supersArray.value.filter((superObject) => superObject.id !== id)
      if (activeSuperObject.value.id === id) {
        activeSuperObject.value = supersArray.value[0]
      }
    } catch (e) {
      throw new Error('Error removing super')
    }
  }

  const resetSuperProductInvestmentOptions = (): void => {
    superProductInvestmentOptions.value = []
  }

  const reset = (): void => {
    supersArray.value = []
    activeSuperObject.value = {} as SuperObject
    superProductInvestmentOptions.value = []
  }

  const checkSuperInvestmentEligibility = (): boolean => {
    const singleSupers = supersArray.value.filter((superObj) => superObj.owner === 'single')

    // If there are no supers with owner 'single', return true (or adjust per your requirements)
    if (singleSupers.length === 0) {
      return false
    }

    // Check each super for a valid balance or available investment options.
    return singleSupers.some((superObj) => {
      const hasInvestmentOptions =
        superObj.investments &&
        Array.isArray(superObj.investments) &&
        superObj.investments.length > 0
      const hasValidBalance = superObj.balance > 0
      return hasInvestmentOptions || hasValidBalance
    })
  }

  return {
    fetchAllSupers,
    addNewSuperObject,
    setupSuper,
    setActiveSuperObject,
    setInvestmentOptionsForSuper,
    updateSuperObject,
    removeSuperObject,
    getSupersArray,
    getPrimarySuperObject,
    getActiveSuperObject,
    getSuperProductInvestmentOptions,
    getSuperGuaranteeData,
    getInvestmentOptionsForActiveSuper,
    resetSuperProductInvestmentOptions,
    reset,
    checkSuperInvestmentEligibility,
  }
})
